<template>
  <b-modal :id="modalId" :title="title" @ok="handleSubmit" @hidden="resetModal" :visible="show"
    @change="(val) => $emit('update:show', val)" size="lg" no-close-on-backdrop>
    <transition name="fade">
      <b-card class="dynamic-form-card shadow-lg animate__animated animate__fadeIn">
        <b-card-body>
          <form @submit.prevent="handleSubmit">
            <b-row>
              <b-col v-for="field in fields" :key="field.name" :md="field.col || 6" class="mb-3">
                <b-form-group :label="field.label" :label-for="field.name">
                  <b-form-input
                    v-if="field.type === 'text' || field.type === 'email' || field.type === 'password' || field.type === 'number'"
                    :id="field.name" :type="field.type" v-model.trim="formData[field.name]"
                    :state="getValidationState(field.name)" @blur="$v.formData[field.name].$touch()"></b-form-input>

                  <b-form-select v-if="field.type === 'select'" :id="field.name" v-model="formData[field.name]"
                    :options="field.options" :state="getValidationState(field.name)"
                    @blur="$v.formData[field.name].$touch()"></b-form-select>

                  <b-form-textarea v-if="field.type === 'textarea'" :id="field.name" v-model="formData[field.name]"
                    :state="getValidationState(field.name)" @blur="$v.formData[field.name].$touch()"
                    rows="3"></b-form-textarea>

                  <date-picker v-if="field.type === 'date'" :id="field.name" v-model="formData[field.name]" valueType="format"
                    :format="field.format || 'YYYY-MM-DD'"
                    :input-class="['form-control', { 'is-invalid': getValidationState(field.name) === false }]"
                    @close="$v.formData[field.name].$touch()"></date-picker>

                  <date-range-picker v-if="field.type === 'daterange'" :id="field.name" v-model="formData[field.name]"
                    :input-class="['form-control', { 'is-invalid': getValidationState(field.name) === false }]"
                    @update="$v.formData[field.name].$touch()"></date-range-picker>

                  <model-list-select v-if="field.type === 'search-select'" :id="field.name" :list="field.options || []"
                    :isDisabled="field.isDisabled || false" v-model="formData[field.name]"
                    :option-value="field.optionValue || 'value'" :option-text="field.optionText || 'text'"
                    :placeholder="field.placeholder || 'Select an item'" @blur.native="$v.formData[field.name].$touch()"
                    :class="{ 'is-invalid': getValidationState(field.name) === false }">
                  </model-list-select>

                  <multi-select v-if="field.type === 'search-multi-select'" :id="field.name" :options="field.options || []"
                    :selected-options="formData[field.name] || []" :placeholder="field.placeholder || 'Select items'"
                    :multiple="true" @select="(items) => handleMultiSelect(items, field.name)"
                    :class="{ 'is-invalid': getValidationState(field.name) === false }">
                  </multi-select>

                  <multiselect v-if="field.type === 'multiselect' || field.type === 'tags'" :id="field.name"
                    v-model="formData[field.name]" :options="field.options || []" :multiple="true"
                    :taggable="field.type === 'tags'" @tag="addTag"
                    :placeholder="field.placeholder || 'Select options'" :label="field.labelKey || 'label'"
                    :track-by="field.trackByKey || 'value'" @close="$v.formData[field.name].$touch()"
                    :class="{ 'is-invalid': getValidationState(field.name) === false }"></multiselect>

                  <b-form-file v-if="field.type === 'file'" :id="field.name" v-model="formData[field.name]"
                    :state="getValidationState(field.name)" :placeholder="field.placeholder || 'Choose a file or drop it here...'"
                    drop-placeholder="Drop file here..." @change="$v.formData[field.name].$touch()"></b-form-file>

                  <b-form-input v-if="field.type === 'color'" :id="field.name" type="color" v-model="formData[field.name]"
                    class="w-100" :state="getValidationState(field.name)" @blur="$v.formData[field.name].$touch()"></b-form-input>
                    
                  <b-form-input v-if="field.type === 'range'" :id="field.name" type="range" :min="field.min || 0"
                    :max="field.max || 100" :step="field.step || 1" v-model="formData[field.name]"
                    :state="getValidationState(field.name)" @change="$v.formData[field.name].$touch()"></b-form-input>

                  <b-form-checkbox v-if="field.type === 'checkbox'" :id="field.name" v-model="formData[field.name]"
                    :state="getValidationState(field.name)" @blur="$v.formData[field.name].$touch()">
                    {{ field.checkboxLabel || '' }}
                  </b-form-checkbox>

                  <b-form-checkbox-group v-if="field.type === 'checkbox-group'" :id="field.name"
                    v-model="formData[field.name]" :options="field.options" :state="getValidationState(field.name)"
                    @blur="$v.formData[field.name].$touch()"></b-form-checkbox-group>

                  <b-form-radio-group v-if="field.type === 'radio'" :id="field.name" v-model="formData[field.name]"
                    :options="field.options" :state="getValidationState(field.name)"
                    @blur="$v.formData[field.name].$touch()"></b-form-radio-group>

                  <div v-if="$v.formData[field.name] && $v.formData[field.name].$error" class="invalid-feedback">
                    <span v-if="!$v.formData[field.name].required">This field is required.</span>
                    <span v-if="!$v.formData[field.name].email">Please enter a valid email address.</span>
                    <span v-if="!$v.formData[field.name].minLength">Must have at least {{
                      $v.formData[field.name].$params.minLength.min }} letters.</span>
                    <span v-if="!$v.formData[field.name].maxLength">Can have at most {{
                      $v.formData[field.name].$params.maxLength.max }} letters.</span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
            <div class="d-flex justify-content-end mt-4">
              <b-button type="submit" variant="primary" size="lg" class="px-5 py-2 shadow-sm" :disabled="$v.$invalid">
                Submit
              </b-button>
            </div>
          </form>
        </b-card-body>
      </b-card>
    </transition>

    <template #modal-footer="{ ok, cancel }">
      <b-button @click="cancel()">Cancel</b-button>
      <b-button variant="primary" @click="ok()" :disabled="$v.$invalid">
        Submit
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { validationMixin } from "vuelidate";
import { ModelListSelect, MultiSelect } from "vue-search-select";
import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";
import DateRangePicker from "vue2-daterange-picker";

export default {
  name: "CommonModal",
  mixins: [validationMixin],
  components: {
    DatePicker,
    DateRangePicker,
    ModelListSelect,
    Multiselect,
    MultiSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
      validator: function (fields) {
        return fields.every(
          (field) => typeof field.name === "string" && typeof field.label === "string"
        );
      },
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {},
      modalId: 'dynamic-modal-' + this._uid
    };
  },
  validations() {
    const rules = { formData: {} };
    this.fields.forEach((field) => {
      if (field.validations) {
        rules.formData[field.name] = field.validations;
      } else {
        rules.formData[field.name] = {};
      }
    });
    return rules;
  },
  watch: {
    initialData: {
      handler(newData) {
        this.formData = Object.assign({}, newData);
        this.fields.forEach(field => {
          if (this.formData[field.name] === undefined) {
            this.$set(this.formData, field.name, field.default || (field.type === 'checkbox' ? false : (['multiselect', 'checkbox-group', 'search-multi-select', 'tags'].includes(field.type) ? [] : (field.type === 'daterange' ? { startDate: null, endDate: null } : null))));
          }
        });
      },
      immediate: true,
      deep: true
    },
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$bvModal.show(this.modalId)
        });
      } else {
        this.$bvModal.hide(this.modalId)
      }
    }
  },
  methods: {
    getValidationState(name) {
      const { $dirty, $error } = this.$v.formData[name] || {};
      return $dirty ? !$error : null;
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.$emit("submit", this.formData);
      this.resetModal();
      this.$emit('update:show', false);
    },
    resetModal() {
      this.formData = {};
      this.fields.forEach(field => {
        this.formData[field.name] = this.initialData[field.name] || (field.type === 'checkbox' ? false : (['multiselect', 'checkbox-group', 'search-multi-select', 'tags'].includes(field.type) ? [] : (field.type === 'daterange' ? { startDate: null, endDate: null } : null)));
      });
      this.$v.$reset();
    },
    handleMultiSelect(items, fieldName) {
      this.$set(this.formData, fieldName, items);
      this.$v.formData[fieldName].$touch();
    },
    addTag(newTag, id) {
      const field = this.fields.find(f => f.name === id);
      const value = this.formData[id] || [];
      if (field && field.options) {
        field.options.push(newTag)
      }
      value.push(newTag)
      this.$v.formData[id].$touch();
    }
  },
};
</script>

<style>
.invalid-feedback {
  display: block;
}
.is-invalid .multiselect__tags {
  border-color: #dc3545;
}
.is-invalid.ui.selection.dropdown {
  border-color: #dc3545;
}
.dynamic-form-card {
  border-radius: 1rem;
  box-shadow: 0 6px 32px 0 rgba(60,60,120,0.10), 0 1.5px 6px 0 rgba(60,60,120,0.10);
  background: #fff;
  margin: 2rem auto;
  max-width: 900px;
  padding: 0;
  transition: box-shadow 0.3s;
}
.dynamic-form-card:hover {
  box-shadow: 0 12px 48px 0 rgba(60,60,120,0.18), 0 3px 12px 0 rgba(60,60,120,0.12);
}
.b-card-body {
  padding: 2.5rem 2rem 2rem 2rem;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>


// WEBPACK FOOTER //
// commonModal.vue?c5cf23bc